/**
 * Select Network Step (Step 1)
 * Choose affiliate program/network to connect
 */

import React, { useState } from 'react';
import { Search, CheckCircle } from 'lucide-react';
import { useAvailableNetworks } from '../../../hooks/useNetworksData';
import type { Step1Data } from '../../../types/networks.types';

interface SelectPlatformProps {
  data: Step1Data;
  onChange: (data: Step1Data) => void;
}

export const SelectPlatform: React.FC<SelectPlatformProps> = ({ data, onChange }) => {
  const { data: networks, isLoading } = useAvailableNetworks();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredNetworks = networks?.filter(network => 
    network.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    network.platform_provider.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectNetwork = (networkId: string) => {
    const network = networks?.find(n => n.id === networkId);
    if (network) {
      // Determine auth method and required credentials based on platform provider
      const platformConfig = getPlatformConfig(network.platform_provider.name);
      
      onChange({
        platform_provider_id: network.id, // This is actually network_id, but keeping field name for compatibility
        platform_name: network.name,
        platform_logo: network.logo || network.platform_provider.logo,
        auth_method: platformConfig.auth_method,
        required_credentials: platformConfig.required_credentials
      });
    }
  };

  // Get platform-specific configuration
  const getPlatformConfig = (platformName: string) => {
    const configs: Record<string, { auth_method: 'username_password' | 'api_key' | 'oauth', required_credentials: string[] }> = {
      'NetRefer': { auth_method: 'username_password', required_credentials: ['username', 'password', 'brand_id_extra'] },
      'MyAffiliates': { auth_method: 'username_password', required_credentials: ['base_url', 'username', 'password'] },
      'Income Access': { auth_method: 'username_password', required_credentials: ['username', 'password'] },
      'ReferOn': { auth_method: 'api_key', required_credentials: ['api_key'] },
      'Affilka': { auth_method: 'api_key', required_credentials: ['api_key', 'base_url'] },
      'HitPath': { auth_method: 'api_key', required_credentials: ['api_key', 'account_id'] },
      'Cellxpert': { auth_method: 'api_key', required_credentials: ['api_key', 'base_url'] },
      'Scaleo': { auth_method: 'api_key', required_credentials: ['api_key', 'api_secret'] },
      'Trackier': { auth_method: 'api_key', required_credentials: ['api_key'] },
      'TUNE': { auth_method: 'api_key', required_credentials: ['api_key', 'network_id'] },
      'Everflow': { auth_method: 'api_key', required_credentials: ['api_key', 'network_id'] },
      'Post Affiliate Pro': { auth_method: 'username_password', required_credentials: ['username', 'password', 'base_url'] },
      'Affise': { auth_method: 'api_key', required_credentials: ['api_key'] },
      'CAKE': { auth_method: 'api_key', required_credentials: ['api_key', 'domain'] },
      'LinkTrust': { auth_method: 'api_key', required_credentials: ['api_key'] },
      'Affiliaxe': { auth_method: 'api_key', required_credentials: ['api_key'] },
      'RavenTrack': { auth_method: 'api_key', required_credentials: ['api_key', 'base_url'] },
    };
    
    return configs[platformName] || { auth_method: 'username_password' as const, required_credentials: ['username', 'password'] };
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="relative">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search networks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          aria-label="Search networks"
        />
      </div>

      {/* Network Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
        {filteredNetworks?.map(network => (
          <button
            key={network.id}
            onClick={() => handleSelectNetwork(network.id)}
            className={`relative p-6 border-2 rounded-lg transition-all duration-200 text-left hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              data.platform_provider_id === network.id
                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            aria-label={`Select ${network.name}`}
          >
            {/* Selected Indicator */}
            {data.platform_provider_id === network.id && (
              <div className="absolute top-3 right-3">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            )}

            {/* Network Logo (fallback to platform logo) */}
            <div className="mb-4 h-12">
              {(network.logo || network.platform_provider.logo) ? (
                <img
                  src={network.logo || network.platform_provider.logo || ''}
                  alt={network.name}
                  className="h-12 object-contain"
                />
              ) : (
                <div className="h-12 flex items-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {network.name}
                  </span>
                </div>
              )}
            </div>

            {/* Network Name */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              {network.name}
            </h3>

            {/* Platform Provider Badge */}
            <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
              {network.platform_provider.logo && (
                <img
                  src={network.platform_provider.logo}
                  alt={network.platform_provider.name}
                  className="h-5 w-5 object-contain"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Platform</p>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {network.platform_provider.name}
                </p>
              </div>
            </div>

            {/* Network Type */}
            <span className="inline-block px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded text-xs font-medium">
              {network.type}
            </span>
          </button>
        ))}
      </div>

      {/* Tip */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        <strong>Tip:</strong> Select the affiliate network you want to connect. You can add multiple accounts from the same network.
      </p>
    </div>
  );
};
